import styled from "@emotion/styled";
import { useState, useRef, useEffect, useCallback } from "react";
import { COLORS, dateFormatter, priceFormatter } from "../../settings/themeSettings";
import { THEME_COLORS } from "../../constants/themeColors";
import { useTableAlignment } from "../../hooks/useTableAlignment";
import "./styledTable.scss";
import {
  TablePagination,
  tablePaginationClasses as classes,
} from '@mui/base/TablePagination';
import { Tooltip } from "@mui/material";
import { Info } from "@mui/icons-material";

// eslint-disable-next-line no-unused-vars
const themeColors = {
  primary: THEME_COLORS.PRIMARY,
  primaryDark: THEME_COLORS.PRIMARY_DARK,
  primaryLight: THEME_COLORS.PRIMARY_LIGHT,
  white: THEME_COLORS.WHITE,
};

// eslint-disable-next-line no-unused-vars
const grey = {
  50: '#F3F6F9',
  100: '#E7EBF0',
  200: '#E0E3E7',
  300: '#CDD2D7',
  400: '#B2BAC2',
  500: '#A0AAB4',
  600: '#6F7E8C',
  700: '#3E5060',
  800: '#2D3843',
  900: '#1A2027',
};

const nameFormatter = (name) => {
  const underscoreRegex = new RegExp("_");
  const numeroRegex = new RegExp("numero");
  if (name === "createdAt") {
    return "Date";
  }
  return name.replace(underscoreRegex, " ").replace(numeroRegex, "N°").replaceAll(/([A-Z])/g, ' $1').trim();
};

const Root = styled('div')(
  () => `
    position: relative;
    flex: 1;
    overflow-y: auto;
    overflow-x: auto;
    max-height: 65vh;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid ${COLORS.gray[200]};
    margin: 0 20px 20px 20px;
    background-color: ${COLORS.background.paper};

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px ${COLORS.primary}40;
    }

    table {
      font-family: "Inter", "Roboto", sans-serif;
      font-size: 0.7rem;
      border-collapse: separate;
      border-spacing: 0;
      width: 100%;
      background-color: ${COLORS.background.paper};
      table-layout: fixed;
    }
  
    td,
    th {
      text-align: left;
      padding: 4px 8px;
      color: ${COLORS.text.primary};
      text-transform: capitalize;
      border-bottom: 1px solid ${COLORS.gray[200]};
      transition: all 0.15s ease-in-out;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Largeurs de colonnes standardisées */
    .col-date { width: 90px; }
    .col-number { width: 60px; }
    .col-price { width: 90px; }
    .col-text { width: 150px; }
    .col-id { width: 50px; }
    .col-actions { width: 80px; }
  
    /* En-têtes figées (Sticky Header) */
    th {
      background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%);
      color: ${COLORS.text.white};
      font-weight: 700;
      font-size: 0.65rem;
      letter-spacing: 0.01em;
      text-transform: uppercase;
      position: sticky;
      top: 0;
      z-index: 20;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      
      &:first-of-type {
        border-top-left-radius: 8px;
      }
      
      &:last-of-type {
        border-top-right-radius: 8px;
      }
    }
    
    tbody tr {
      transition: all 0.15s ease-in-out;
      cursor: pointer;
      
      &:hover {
        background-color: ${COLORS.gray[50]};
      }

      /* Ligne active en surbrillance (Note Technique AFT) */
      &.row-selected, &.row-active {
        background-color: #edf3fc !important;
      }

      &.row-selected td:first-of-type, &.row-active td:first-of-type {
        border-left: 3px solid ${COLORS.primary} !important;
      }
      
      &:last-child td {
        border-bottom: none;
      }

      &:focus {
        outline: none;
        background-color: #edf3fc !important;
      }
    }
    
    td {
      font-weight: 500;
      
      &:first-of-type {
        font-weight: 600;
        color: ${COLORS.primary};
      }
    }
  `,
);

const CustomTablePagination = styled(TablePagination)(
  () => `
    & .${classes.spacer} {
      display: none;
    }
  
    & .${classes.toolbar}  {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
      padding: 12px 20px;
      background-color: ${COLORS.background.paper};
      border-top: 1px solid ${COLORS.gray[200]};
      color: ${COLORS.text.primary};
  
      @media (min-width: 768px) {
        flex-direction: row;
        align-items: center;
      }
    }
  
    & .${classes.selectLabel} {
      color: ${COLORS.text.secondary};
      margin: 0;
      font-weight: 500;
      font-size: 0.8rem;
    }
  
    & .${classes.select}{
      padding: 4px 8px;
      border: 1px solid ${COLORS.gray[300]};
      border-radius: 6px;
      background-color: ${COLORS.background.paper};
      color: ${COLORS.text.primary};
      font-weight: 500;
      transition: all 0.2s ease-in-out;
  
      &:hover {
        border-color: ${COLORS.primary};
        background-color: ${COLORS.gray[50]};
      }
  
      &:focus {
        outline: none;
        border-color: ${COLORS.primary};
        box-shadow: 0 0 0 2px ${COLORS.primary}20;
      }
    }
  
    & .${classes.displayedRows} {
      margin: 0;
      color: ${COLORS.text.secondary};
      font-weight: 500;
      font-size: 0.8rem;
      @media (min-width: 768px) {
        margin-left: auto;
      }
    }
  
    & .${classes.actions} {
      padding: 2px;
      border: 1px solid ${COLORS.gray[300]};
      border-radius: 6px;
      text-align: center;
      background-color: ${COLORS.background.paper};
    }
  
    & .${classes.actions} > button {
      margin: 0 2px;
      border: none;
      border-radius: 4px;
      background-color: transparent;
      color: ${COLORS.text.secondary};
      padding: 4px 6px;
      transition: all 0.2s ease-in-out;
      
      &:hover {
        background-color: ${COLORS.primary};
        color: ${COLORS.text.white};
      }
  
      &:focus {
        outline: none;
        background-color: ${COLORS.primary};
        color: ${COLORS.text.white};
      }
      
      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        
        &:hover {
          background-color: transparent;
          color: ${COLORS.text.secondary};
        }
      }
    }
  `,
);

const StyledTable = ({ 
  head = [], 
  rows = [], 
  onOptionClicked, 
  onRowDoubleClick, 
  headLabels = {}, 
  customRenderers = {}, 
  hideOptions = false, 
  disableScroll = false,
  maxHeight = '65vh'
}) => {
  const showOptions = !hideOptions && typeof onOptionClicked === 'function';

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRowIdx, setSelectedRowIdx] = useState(0);
  const tableRef = useRef(null);
  const rowRefs = useRef([]);

  const currentRows = rowsPerPage > 0
    ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : rows;

  const handleRowAction = useCallback((row) => {
    if (!row) return;
    if (onRowDoubleClick) {
      onRowDoubleClick(row.id, row);
    } else if (onOptionClicked) {
      onOptionClicked(row.id, row);
    }
  }, [onRowDoubleClick, onOptionClicked]);

  // Navigation clavier dans la liste (↑ / ↓ et Entrée)
  const handleTableKeyDown = (e) => {
    if (currentRows.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedRowIdx((prev) => {
        const next = Math.min(prev + 1, currentRows.length - 1);
        if (rowRefs.current[next]) {
          rowRefs.current[next].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedRowIdx((prev) => {
        const next = Math.max(prev - 1, 0);
        if (rowRefs.current[next]) {
          rowRefs.current[next].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
        return next;
      });
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const targetRow = currentRows[selectedRowIdx];
      if (targetRow) {
        handleRowAction(targetRow);
      }
    }
  };

  // Réinitialiser la sélection lors du changement de page
  useEffect(() => {
    setSelectedRowIdx(0);
  }, [page, rowsPerPage]);

  useTableAlignment(tableRef, rows, {
    autoAlign: true,
    autoColor: true,
    autoWidth: true,
    applyHover: true
  });

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getColumnClass = (title) => {
    if (/date|createdAt/i.test(title)) return 'col-date';
    if (/montant|prix|tarif|depouillement|marge/i.test(title)) return 'col-price';
    if (/pax|nuitee|nombre|jour/i.test(title)) return 'col-number';
    if (title === 'id' || title === '_id') return 'col-id';
    return 'col-text';
  };

  return (
    <Root 
      sx={{ 
        width: '100%', 
        maxWidth: '100%', 
        maxHeight: maxHeight,
        ...(disableScroll ? { maxHeight: 'none', overflowY: 'visible', boxShadow: 'none', border: 'none' } : {}) 
      }} 
      ref={tableRef}
      tabIndex={0}
      onKeyDown={handleTableKeyDown}
      aria-label="Tableau de données navigable au clavier"
    >
      <table aria-label="custom pagination table">
        <thead>
          <tr>
            {head.map((title, idx) => {
              const label = nameFormatter(headLabels.hasOwnProperty(title) ? headLabels[title] : title);
              return (
                <th key={idx} className={getColumnClass(title)} style={{ textAlign: 'center' }}>
                  <Tooltip title={label} arrow>
                    <span>{label}</span>
                  </Tooltip>
                </th>
              );
            })}
            {showOptions && <th className="col-actions" style={{ textAlign: 'center' }}>Options</th>}
          </tr>
        </thead>
        <tbody>
          {currentRows.map((row, idx) => (
            <tr 
              key={idx}
              ref={(el) => (rowRefs.current[idx] = el)}
              className={selectedRowIdx === idx ? 'row-selected' : ''}
              onClick={() => {
                setSelectedRowIdx(idx);
                if (onOptionClicked) onOptionClicked(row.id, row);
              }}
              onDoubleClick={() => handleRowAction(row)}
              style={{ cursor: (onOptionClicked || onRowDoubleClick) ? 'pointer' : 'default' }}
            >
              {head.map((title, titleIdx) => {
                const value = customRenderers[title] 
                  ? customRenderers[title](row[title], row) 
                  : (/montant|prix|tarif|depouillement|marge$/.test(title) 
                      ? priceFormatter(row[title]) 
                      : ((/date|createdAt/.test(title)) ? dateFormatter(row[title]) : row[title]));
                return (
                  <td key={titleIdx} className={getColumnClass(title)} align="right">
                    <Tooltip title={String(value || "")} arrow>
                      <span>{value}</span>
                    </Tooltip>
                  </td>
                );
              })}

              {showOptions && (
                <td 
                  className="col-actions" 
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: '8px',
                    border: 'none'
                  }}
                >
                  <div 
                    style={{
                      padding: '6px',
                      borderRadius: '6px',
                      backgroundColor: `${COLORS.primary}15`,
                      transition: 'all 0.2s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${COLORS.primary}25`;
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = `${COLORS.primary}15`;
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <Info
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onOptionClicked) onOptionClicked(row.id, row);
                      }}
                      style={{
                        fill: COLORS.primary,
                        fontSize: '1.1rem',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                </td>
              )}
            </tr>
          ))}

          {emptyRows > 0 && (
            <tr style={{ height: 34 * emptyRows }}>
              <td colSpan={head.length + (showOptions ? 1 : 0)} />
            </tr>
          )}

          {currentRows.length === 0 && (
            <tr>
              <td 
                colSpan={head.length + (showOptions ? 1 : 0)} 
                style={{ textAlign: 'center', color: '#999', padding: '24px', fontStyle: 'italic' }}
              >
                Aucune donnée disponible
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <CustomTablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'Tous', value: -1 }]}
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              labelRowsPerPage={"Lignes par page"}
              slotProps={{
                select: {
                  'aria-label': 'lignes par page',
                },
                actions: {
                  showFirstButton: true,
                  showLastButton: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </tr>
        </tfoot>
      </table>
    </Root>
  );
};

export default StyledTable;
